import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { api, type UserProfile } from '../lib/api';

export function Settings() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMe()
      .then(setProfile)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleRotateKey = async () => {
    if (!confirm('Are you sure? This will invalidate your current API key.')) return;
    setIsRotating(true);
    try {
      const result = await api.rotateKey();
      setApiKey(result.apiKey);
      setShowKey(true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsRotating(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="deploy-spinner" />
      </div>
    );
  }

  const instanceName = profile?.instance?.name ?? 'unknown';

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <span className="logo-icon">🦞</span>
          <div>
            <div className="logo-text">CLAWZEMPIC</div>
            <div className="logo-sub">The Inference Diet</div>
          </div>
        </div>

        <div className="nav">
          <Link to="/dashboard" className="nav-item">
            <span className="nav-icon">📊</span>
            Overview
          </Link>
          <a
            href={profile?.instance?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
          >
            <span className="nav-icon">💬</span>
            Chat
          </a>
          <Link to="/settings" className="nav-item active">
            <span className="nav-icon">⚙️</span>
            Settings
          </Link>
        </div>

        <div className="sidebar-footer">
          <a href="#">Privacy</a>
          <span className="footer-sep">|</span>
          <a href="#">Terms</a>
          <br />
          &copy; 2026 Clawzempic
        </div>
      </div>

      {/* Main content */}
      <div className="dashboard-main">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Manage your instance configuration</p>
          </div>
          <UserButton />
        </div>

        {/* Instance info */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <div className="card-title">Instance</div>
            <span className={`badge ${profile?.instance?.status === 'running' ? 'badge-green' : 'badge-muted'}`}>
              {profile?.instance?.status ?? 'none'}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="input-group">
              <label className="input-label">Name</label>
              <div className="input-field" style={{ background: 'var(--bg-active)', cursor: 'default' }}>
                {instanceName}
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">URL</label>
              <div className="input-field" style={{ background: 'var(--bg-active)', cursor: 'default', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                {instanceName}.clawzempic.com
              </div>
            </div>
          </div>
        </div>

        {/* API Key */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <div className="card-title">API Key</div>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
            Use this key to connect to the Clawzempic proxy from your own tools.
          </p>

          <div className="input-group">
            <label className="input-label">Proxy URL</label>
            <div className="input-field" style={{ background: 'var(--bg-active)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
              https://{instanceName}.clawzempic.com/v1
            </div>
          </div>

          {apiKey && showKey ? (
            <div className="input-group">
              <label className="input-label">New API Key (save it now — shown only once)</label>
              <div
                className="input-field"
                style={{
                  background: 'rgba(52, 211, 153, 0.05)',
                  border: '1px solid var(--green)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  wordBreak: 'break-all',
                }}
              >
                {apiKey}
              </div>
            </div>
          ) : (
            <div className="input-group">
              <label className="input-label">API Key</label>
              <div className="input-field" style={{ background: 'var(--bg-active)', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)' }}>
                sr_••••••••••••••••
              </div>
            </div>
          )}

          <button
            className="btn btn-secondary"
            onClick={handleRotateKey}
            disabled={isRotating}
          >
            {isRotating ? 'Rotating...' : 'Rotate API Key'}
          </button>
        </div>

        {/* Account */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Account</div>
          </div>
          <div className="input-group">
            <label className="input-label">Email</label>
            <div className="input-field" style={{ background: 'var(--bg-active)', cursor: 'default' }}>
              {profile?.user?.email ?? '—'}
            </div>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
            Managed by Clerk. Click your avatar to update account settings.
          </p>
        </div>
      </div>
    </div>
  );
}
